import { isApiError, isUploadError } from "@sagot/api-client";
import { ADDON_MAX_UNITS, ADDON_PARAM, KNOWLEDGE_STORAGE_FULL_REASON, PLAN_LIMIT_REASON_KEY, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Icon } from "@sagot/ui";
import { useId } from "react";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useAddonCatalog } from "../../workspace/shared";
import { useCanManageBilling } from "../../workspace/billing/use-billing";
import { useBillingOverview } from "./use-shared-queries";

const PAYMENT_REQUIRED = 402;
const KNOWLEDGE_STORAGE_ADDON = "knowledge_storage_1gb";

/** True for the 402 the API answers when the knowledge storage meter is full (also through an upload failure). */
export function isKnowledgeStorageFull(error: unknown): boolean {
  const api = isApiError(error) ? error : isUploadError(error) && isApiError(error.cause) ? error.cause : undefined;
  return api?.status === PAYMENT_REQUIRED && api.details[PLAN_LIMIT_REASON_KEY] === KNOWLEDGE_STORAGE_FULL_REASON;
}

/**
 * Whether "Add storage" leads somewhere: the member manages billing, an admin has the add-on on
 * sale for this plan, and the workspace is below the add-on's cap. Otherwise only "Compare plans".
 */
export function useStorageAddonOffer(): boolean {
  const canManage = useCanManageBilling();
  const catalog = useAddonCatalog(canManage);
  const overview = useBillingOverview();
  if (!canManage || !catalog.data || !overview.data) return false;
  const listed = catalog.data.find((addon) => addon.key === KNOWLEDGE_STORAGE_ADDON);
  if (!listed?.available_plans.includes(overview.data.plan.key)) return false;
  const held = overview.data.addons.filter((addon) => addon.key === KNOWLEDGE_STORAGE_ADDON).reduce((total, addon) => total + addon.quantity, 0);
  return held < (ADDON_MAX_UNITS[KNOWLEDGE_STORAGE_ADDON] ?? 0);
}

/** "Add storage" (owners and admins, when on sale) and "Compare plans" for a full storage meter. */
export function StorageFullActions({ size = "sm" }: { size?: "sm" | "md" }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const offer = useStorageAddonOffer();
  const billing = consoleRoutes.billing(summary.slug);
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {offer ? (
        <Button asChild size={size}>
          <AppLink href={`${billing}?${ADDON_PARAM}=${KNOWLEDGE_STORAGE_ADDON}`}>
            <Icon name="database" />
            {t("deployKnowledge.storageFull.add")}
          </AppLink>
        </Button>
      ) : null}
      <Button asChild size={size} variant="secondary">
        <AppLink href={billing}>
          <Icon name="arrow-up-right" />
          {t("deployKnowledge.planGate.upgrade")}
        </AppLink>
      </Button>
    </span>
  );
}

/** The knowledge page's answer to a full storage meter: what happened, what still works, what to do. */
export function StorageFullCard() {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <section className="card card-body flex flex-col gap-4 md:flex-row md:items-center" aria-labelledby={titleId} data-testid="storage-full">
      <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent-ink">
        <Icon name="database" className="size-6" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 id={titleId} className="text-base font-bold">
          {t("deployKnowledge.storageFull.title")}
        </h2>
        <p className="mt-1 text-sm text-fg-muted">{t("deployKnowledge.storageFull.body")}</p>
      </div>
      <StorageFullActions size="md" />
    </section>
  );
}
